import clsx from 'clsx';
import React from 'react';
import Button from '../../components/Button';
import { toggleSwitch } from '../../reducers/switch';
import ToggleIcon from '../../assets/icons/sidebar-toggle-icon.svg?react';
import { useDispatch } from 'react-redux';
import {
  GetReportsForOshaResponse,
  InvestigationClassifyIllnessEnum,
  InvestigationReportingControllerFindAllInjuriesIncidentTypeOfLossEnum,
  InvestigationTypeEnum,
  TypeOfLossChartLabelEnum,
} from '@EHSNavigator/backend';

interface CreateOshaReportStepTwoProps {
  otherClasses?: string;
  handleSteps: (steps: number) => void;
  data: GetReportsForOshaResponse['reports'];
}

const CreateOshaReportStepTwo: React.FC<CreateOshaReportStepTwoProps> = ({
  otherClasses,
  handleSteps,
  data,
}) => {
  const createOshaReportStepTwoClasses = clsx(otherClasses);
  const dispatch = useDispatch();
  function get300FormData(
    investigations: GetReportsForOshaResponse['reports'],
  ) {
    const deaths = investigations?.filter(
      (node) => node?.typeOfLoss === TypeOfLossChartLabelEnum.Death,
    )?.length;

    const awayfromWorkCases = investigations?.filter(
      (node) => node?.typeOfLoss === TypeOfLossChartLabelEnum.DaysAwayFromWork,
    )?.length;

    const jobTransferCases = investigations?.filter(
      (node) =>
        node?.typeOfLoss ===
        TypeOfLossChartLabelEnum.RestrictedWorkActivityOrJobTransfer,
    )?.length;

    const otherCasesRecorable =
      investigations?.length - deaths - awayfromWorkCases - jobTransferCases;

    const injuries = investigations?.filter(
      (node) => node?.type === InvestigationTypeEnum.Injury,
    )?.length;

    const skin = investigations?.filter(
      (node) =>
        node?.type === InvestigationTypeEnum.Illness &&
        node?.classifyIllness === InvestigationClassifyIllnessEnum.SkinDisorder,
    )?.length;

    const respiratory = investigations?.filter(
      (node) =>
        node?.type === InvestigationTypeEnum.Illness &&
        node?.classifyIllness ===
          InvestigationClassifyIllnessEnum.RespiratoryCondition,
    )?.length;

    const poisonings = investigations?.filter(
      (node) =>
        node?.type === InvestigationTypeEnum.Illness &&
        node?.classifyIllness === InvestigationClassifyIllnessEnum.Poisoning,
    )?.length;

    const hearing = investigations?.filter(
      (node) =>
        node?.type === InvestigationTypeEnum.Illness &&
        node?.classifyIllness === InvestigationClassifyIllnessEnum.HearingLoss,
    )?.length;

    const otherIllness = investigations?.filter(
      (node) =>
        node?.type === InvestigationTypeEnum.Illness &&
        node?.classifyIllness === InvestigationClassifyIllnessEnum.OtherIllness,
    )?.length;

    const noOfDaysAwayFromWork = investigations
      ?.filter(
        (node) =>
          node?.typeOfLoss ===
          InvestigationReportingControllerFindAllInjuriesIncidentTypeOfLossEnum.DaysAwayFromWork,
      )
      ?.reduce((accumulator, currentValue) => {
        return accumulator + Number(currentValue?.daysAwayFromWork);
      }, 0);

    const noOfDaysJobTransfer = investigations
      ?.filter(
        (node) =>
          node?.typeOfLoss ===
          InvestigationReportingControllerFindAllInjuriesIncidentTypeOfLossEnum.RestrictedWorkActivityOrJobTransfer,
      )
      ?.reduce((accumulator, currentValue) => {
        return accumulator + Number(currentValue?.jobRestrictionDays);
      }, 0);

    const no_injuries_illnesses = investigations?.length > 0 ? 1 : 2;

    return {
      no_injuries_illnesses,
      total_deaths: deaths,
      total_dafw_cases: awayfromWorkCases,
      total_djtr_cases: jobTransferCases,
      total_other_cases: otherCasesRecorable,
      total_dafw_days: noOfDaysAwayFromWork,
      total_djtr_days: noOfDaysJobTransfer,
      total_injuries: injuries,
      total_skin_disorders: skin,
      total_respiratory_conditions: respiratory,
      total_poisonings: poisonings,
      total_hearing_loss: hearing,
      total_other_illnesses: otherIllness,
    };
  }

  const stats = get300FormData(data);

  return (
    <div
      className={createOshaReportStepTwoClasses}
      data-testid="create-osha-report-step-two"
    >
      <div className="flex lg:gap-10 xl:items-center xl:justify-between">
        <div className="flex flex-1 items-start gap-2.5">
          <button
            type="button"
            className="bg-translucent-background/80 border-neutral-tones-text/30 rounded-5 cursor-pointer border p-0.5 lg:hidden"
            onClick={() => dispatch(toggleSwitch())}
          >
            <ToggleIcon />
          </button>
          <div>
            <h1 className="h3 mb-1 lg:mb-0">OSHA 300A</h1>
            <p className="text-brand-byzantine font-inter-variable -tracking-button text-base leading-5 font-bold lg:text-xl lg:leading-6">
              STEP 2 / 3
            </p>
          </div>
        </div>
        <div className="hidden items-center gap-15 lg:flex">
          <Button
            type="button"
            size="smaller"
            variant="secondary"
            onClick={() => handleSteps(1)}
            otherClasses="w-174 text-brand-posh-pink  !border-brand-posh-pink hover:text-white  hover:!bg-brand-posh-pink"
          >
            Previous
          </Button>
          <Button
            type="button"
            onClick={() => handleSteps(3)}
            size="smaller"
            otherClasses="w-174"
          >
            Proceed
          </Button>
        </div>
      </div>
      <p className="font-inter-variable text-neutral-tones-text/80 -tracking-button mt-8 text-sm leading-4 font-medium lg:mt-15 lg:text-xl lg:leading-6">
        This is a summary of the data reported in OSHA 300 form. All data will
        be submitted to OSHA after this step.
      </p>
      <p className="font-inter-variable text-neutral-tones-text/80 -tracking-button mt-8 text-sm leading-4 font-medium lg:mt-15 lg:text-xl lg:leading-6">
        Ensure accuracy when submitting to OSHA - incorrect or fraudulent data
        may incur penalties.
      </p>
      <div className="mt-9 grid max-w-1050 grid-cols-1 gap-8 md:grid-cols-12 lg:gap-9">
        <div className="border-neutral-tones-text/35 rounded-10 bg-card-backgrounds/5 flex flex-col overflow-hidden border md:col-span-7 lg:gap-8 xl:col-span-8">
          <div className="bg-brand-byzantine w-full p-15">
            <h4 className="text-neutral-tones-text-revert">Number of Cases</h4>
          </div>
          <div className="flex flex-wrap justify-between gap-y-5 px-15 pt-15 pb-15 lg:gap-y-9 lg:pt-0">
            <div className="flex w-full max-w-275 flex-col">
              <h4 className="text-brand-byzantine">{stats?.total_deaths}</h4>
              <p className="text-neutral-tones-text font-inter-variable text-sm leading-5 font-bold uppercase lg:text-base">
                TOTAL NUMBER OF DEATHS
              </p>
            </div>
            <div className="flex w-full max-w-275 flex-col">
              <h4 className="text-brand-byzantine">
                {stats?.total_dafw_cases}
              </h4>
              <p className="text-neutral-tones-text font-inter-variable text-sm leading-5 font-bold uppercase lg:text-base">
                TOTAL NUMBER OF CASES WITH DAYS AWAY FROM WORK
              </p>
            </div>
            <div className="flex w-full max-w-275 flex-col">
              <h4 className="text-brand-byzantine">
                {stats?.total_djtr_cases}
              </h4>
              <p className="text-neutral-tones-text font-inter-variable text-sm leading-5 font-bold uppercase lg:text-base">
                TOTAL NUMBER OF CASES WITH JOB TRANSFER OR RESTRICTION
              </p>
            </div>
            <div className="flex w-full max-w-275 flex-col">
              <h4 className="text-brand-byzantine">
                {stats?.total_other_cases}
              </h4>
              <p className="text-neutral-tones-text font-inter-variable text-sm leading-5 font-bold uppercase lg:text-base">
                TOTAL NUMBER OF OTHER RECORDABLE CASES
              </p>
            </div>
          </div>
        </div>
        <div className="border-neutral-tones-text/35 rounded-10 bg-card-backgrounds/5 flex flex-col overflow-hidden border md:col-span-5 lg:gap-8 xl:col-span-4">
          <div className="bg-brand-byzantine w-full p-15">
            <h4 className="text-neutral-tones-text-revert">Number of Days</h4>
          </div>
          <div className="flex flex-wrap justify-between gap-y-5 px-15 pt-15 pb-15 md:flex-col lg:gap-y-9 lg:pt-0">
            <div className="flex w-full max-w-275 flex-col">
              <h4 className="text-brand-byzantine">{stats?.total_dafw_days}</h4>
              <p className="text-neutral-tones-text font-inter-variable text-sm leading-5 font-bold uppercase lg:text-base">
                TOTAL NUMBER OF DAYS AWAY FROM WORK
              </p>
            </div>
            <div className="flex w-full max-w-275 flex-col">
              <h4 className="text-brand-byzantine">{stats?.total_djtr_days}</h4>
              <p className="text-neutral-tones-text font-inter-variable text-sm leading-5 font-bold uppercase lg:text-base">
                TOTAL NUMBER OF JOB TRANSFER OR RESTRICTION
              </p>
            </div>
          </div>
        </div>
        <div className="border-neutral-tones-text/35 rounded-10 bg-card-backgrounds/5 flex flex-col overflow-hidden border md:col-span-7 lg:gap-8 xl:col-span-8">
          <div className="bg-brand-byzantine w-full p-15">
            <h4 className="text-neutral-tones-text-revert">
              Injury and Illness Types
            </h4>
          </div>
          <div className="flex flex-wrap justify-between gap-y-9 px-15 pt-15 pb-15 lg:pt-0">
            <div className="flex w-full max-w-215 flex-col">
              <h4 className="text-brand-byzantine">{stats?.total_injuries}</h4>
              <p className="text-neutral-tones-text font-inter-variable text-sm leading-5 font-bold uppercase lg:text-base">
                INJURIES
              </p>
            </div>
            <div className="flex w-full max-w-215 flex-col">
              <h4 className="text-brand-byzantine">
                {stats?.total_skin_disorders}
              </h4>
              <p className="text-neutral-tones-text font-inter-variable text-sm leading-5 font-bold uppercase lg:text-base">
                SKIN DISORDERS
              </p>
            </div>
            <div className="flex w-full max-w-215 flex-col">
              <h4 className="text-brand-byzantine">
                {stats?.total_respiratory_conditions}
              </h4>
              <p className="text-neutral-tones-text font-inter-variable text-sm leading-5 font-bold uppercase lg:text-base">
                RESPIRATORY CONDITIONS
              </p>
            </div>
            <div className="flex w-full max-w-215 flex-col">
              <h4 className="text-brand-byzantine">
                {stats?.total_poisonings}
              </h4>
              <p className="text-neutral-tones-text font-inter-variable text-sm leading-5 font-bold uppercase lg:text-base">
                POISONINGS
              </p>
            </div>
            <div className="flex w-full max-w-215 flex-col">
              <h4 className="text-brand-byzantine">
                {stats?.total_hearing_loss}
              </h4>
              <p className="text-neutral-tones-text font-inter-variable text-sm leading-5 font-bold uppercase lg:text-base">
                HEARING LOSS
              </p>
            </div>
            <div className="flex w-full max-w-215 flex-col">
              <h4 className="text-brand-byzantine">
                {stats?.total_other_illnesses}
              </h4>
              <p className="text-neutral-tones-text font-inter-variable text-sm leading-5 font-bold uppercase lg:text-base">
                ALL OTHER ILLNESSES
              </p>
            </div>
          </div>
        </div>
      </div>
      <div className="mt-8 flex items-center justify-center gap-15 lg:hidden">
        <Button
          size="smaller"
          variant="secondary"
          type="button"
          onClick={() => handleSteps(1)}
          otherClasses="w-2/4 sm:w-174 text-brand-posh-pink  !border-brand-posh-pink hover:text-white  hover:!bg-brand-posh-pink"
        >
          Cancel
        </Button>
        <Button
          type="button"
          onClick={() => handleSteps(3)}
          size="smaller"
          otherClasses="w-2/4 sm:w-174"
        >
          Proceed
        </Button>
      </div>
    </div>
  );
};

export default CreateOshaReportStepTwo;
